import el from "@cypherpotato/el";
import Color from "colorjs.io";
import { api } from "../../api";
import { attachmentsToContentParts, ChatV2 } from "../../components/chat.v2";
import { DropOverlay } from "../../components/drop-overlay";
import { brigtenColor, darkenColor } from "../../ui/color";
import { runOperation } from "../../ui/operation";

export default function (ctx) {

    const chatClientKey = ctx.params.key;
    const chatClientData = el.state.create(null);

    var playingAudio;
    var abortController;
    var activeSse;
    var activeInferenceRetryTimeout;
    var currentAssistantMessage;
    var chat;
    var neutralThemeStyles = el("style");
    var colorStyles = el("style");
    var customStyles = el("style");

    neutralThemeStyles.textContent = `
        @media (prefers-color-scheme: light) {
            :root {
                --primary-color: #2079c2;
                --primary-color-100: #2d88d3;
                --primary-color-200: #3d97e0;
                --primary-color-opaque: #2079c244;
                --contrast: #fff;
                --bg-000: #fff;
                --bg-100: #fcfcfc;
                --bg-200: #f4f4f4;
                --bg-300: #e4e4e4;
                --bg-400: #d3d3db;
                --bg-opaque: #fcfcfca8;
                --bg-opaque-200: #e2e2e264;
                --item-hover: #77777711;
                --item-active: #7777771a;
                --bg-dark-opaque: #33333333;
                --border-color: #e4e4e4;
                --fr-100: #090909;
                --fr-200: #535353;
                --fr-300: #8a8a8a;
                --warn: #8c342f;
                --success: #187718;
                --info: #007bff;
                --link-color: var(--primary-color);
                --tint-mask: url('/assets/tint-light.png');
                color-scheme: light;
            }
        }

        @media (prefers-color-scheme: light) and (max-width: 700px) {
            :root {
                --bg-opaque: #ffffff99;
                --bg-dark-opaque: #33333388;
            }
        }

        @media (prefers-color-scheme: dark) {
            :root {
                --primary-color: #2380cc;
                --primary-color-100: #1164a8;
                --primary-color-200: #054980;
                --primary-color-opaque: #146bb333;
                --contrast: #000;
                --bg-000: #101010;
                --bg-100: #1a1a1b;
                --bg-200: #202020;
                --bg-300: #2c2c2c;
                --bg-400: #3f3f3f;
                --bg-opaque: #000000b2;
                --bg-opaque-200: #29292988;
                --item-hover: #aaaaaa11;
                --item-active: #aaaaaa1a;
                --bg-dark-opaque: #eeeeee33;
                --border-color: #333333;
                --fr-100: #f5f5f4;
                --fr-200: #bbbbbb;
                --fr-300: #6c6c6c;
                --warn: #eda074;
                --success: #0da70d;
                --info: #47a0ff;
                --link-color: #50a7ee;
                --tint-mask: url('/assets/tint-dark.png');
                color-scheme: dark;
            }
        }

        @media (prefers-color-scheme: dark) and (max-width: 700px) {
            :root {
                --bg-opaque: #09090b88;
                --bg-dark-opaque: #eeeeee77;
            }
        }
    `;

    async function reloadMessages() {
        await runOperation(null, async () => {
            const response = await api.getJson(`/api/v1/public/chat-clients/${chatClientKey}`);
            chatClientData.value = response.data;

            const client = chatClientData.value.client;

            if (client.customStyles) {
                customStyles.innerHTML = client.customStyles;
            }
            if (client.primaryColor) {
                const lightColors = brigtenColor(client.primaryColor, 3);
                const darkColors = darkenColor(client.primaryColor, 3);
                const userBubbleBg = darkColors[0];
                const userBubbleColor = new Color(userBubbleBg);
                const userBubbleFr = Math.abs(userBubbleColor.contrast("white", "APCA")) >= Math.abs(userBubbleColor.contrast("black", "APCA"))
                    ? "#fff"
                    : "#000";

                colorStyles.innerHTML = `
                    @media (prefers-color-scheme: light) {
                        :root {
                            --primary-color: ${lightColors[0]};
                            --primary-color-100: ${lightColors[1]};
                            --primary-color-200: ${lightColors[2]};
                            --primary-color-opaque: ${lightColors[0]}33;
                            --user-bubble-bg: ${userBubbleBg};
                            --user-bubble-fr: ${userBubbleFr};
                        }
                    }
                    
                    @media (prefers-color-scheme: dark) {
                        :root {
                            --primary-color: ${darkColors[0]};
                            --primary-color-100: ${darkColors[1]};
                            --primary-color-200: ${darkColors[2]};
                            --primary-color-opaque: ${lightColors[0]}33;
                            --user-bubble-bg: ${userBubbleBg};
                            --user-bubble-fr: ${userBubbleFr};
                        }
                    }
                `;
            }
            if (client.customScripts) {
                new Function(client.customScripts)();
            }
            if (client.pageTitle) {
                document.title = client.pageTitle;
            }
        });
    }

    async function handleClear() {
        runOperation(null, async () => {
            await api.postJson(`/api/v1/public/chat-clients/${chatClientKey}/reset-state`);
            await reloadMessages();
        });
    }

    async function handleSinthetize({ sender, state, text }) {
        if (state == "play") {
            sender.setPlayingState("loading");
            const audioSrc = window.env.apiEndpoint + `/api/v1/public/chat-clients/${chatClientKey}/sinthetize?t=${encodeURIComponent(text)}`;
            playingAudio = new Audio(audioSrc);
            playingAudio.play();
            playingAudio.onended = function () {
                sender.setPlayingState(null);
            }
            playingAudio.onpaused = function () {
                sender.setPlayingState(null);
            }
            playingAudio.onplay = function () {
                sender.setPlayingState("playing");
            }
        } else if (state == "stop") {
            sender.setPlayingState(null);
            playingAudio?.pause();
        }
    }

    async function startInference(assistantMessage) {
        const maxAttempts = 3;
        var isFinished = false;
        var attempt = 0;
        const finished = () => {
            if (isFinished) {
                return;
            }

            isFinished = true;
            clearTimeout(activeInferenceRetryTimeout);
            assistantMessage.setDone();
            chat.isReadOnly = false;
            chat.isInferencing = false;
            chat.renderEnhancements();
            activeSse = null;
            activeInferenceRetryTimeout = null;
            currentAssistantMessage = null;
        };

        const connect = () => {
            attempt++;
            var attemptFinished = false;
            const sse = api.createEventSource(`/api/v1/public/chat-clients/${chatClientKey}/sse`);
            activeSse = sse;

            const retry = () => {
                if (attemptFinished || isFinished) {
                    return;
                }

                attemptFinished = true;
                sse.close();
                activeSse = null;

                if (attempt < maxAttempts) {
                    assistantMessage.setError(null);
                    assistantMessage.setContent("");
                    assistantMessage.startImplicitThinking();
                    activeInferenceRetryTimeout = setTimeout(connect, 1000);
                    return;
                }

                assistantMessage.setError(chatClientData.value.culture.inferenceError);
                finished();
            };

            sse.onerror = retry;
            sse.onmessage = e => {
                if (e.data && e.data.startsWith("{")) {
                    const payload = JSON.parse(e.data);
                    const data = payload.data;

                    if (payload.type == "chat.chunk" && data.chunk) {
                        if (data.type == "reasoning") {
                            assistantMessage.appendReasoning(data.chunk);
                        } else {
                            if (data.insertMode == "append") {
                                assistantMessage.append(data.chunk);
                            } else if (data.insertMode == "replace") {
                                assistantMessage.setContent(data.chunk);
                            }
                        }
                        chat.scrollToBottom();

                    } else if (payload.type == "chat.debug-info") {
                        console.log(`%c${payload.data.information}`, payload.data.style);

                    } else if (payload.type == "chat.error") {
                        assistantMessage.setError(chatClientData.value.culture.inferenceError);

                    } else if (payload.type == "chat.inlineerror") {
                        assistantMessage.setError(payload.data.error);

                    } else if (payload.type == "chat.start-thinking") {
                        assistantMessage.startImplicitThinking();
                    } else if (payload.type == "chat.stop") {
                        attemptFinished = true;
                        sse.close();
                        finished();
                    }
                } else if (e.data == "[DONE]") {
                    retry();
                }
            };
        };

        connect();
    }

    async function retryContext(message) {
        message && message.remove && message.remove();

        await api.postJson(`/api/v1/public/chat-clients/${chatClientKey}/retry`);
        await reloadMessages();

        chat.scrollToBottom();
        const assistantMessage = chat.createMessage("assistant");
        assistantMessage.startImplicitThinking();
        currentAssistantMessage = assistantMessage;
        chat.isInferencing = true;
        chat.isReadOnly = true;

        await startInference(assistantMessage);
    }

    async function handleMessage(message, attachments) {
        if (chat.isInferencing) {
            return;
        }

        abortController = new AbortController();
        chat.isReadOnly = true;
        chat.isInferencing = true;
        const safeAttachments = attachments || [];
        const userContent = attachmentsToContentParts(message, safeAttachments);

        chat.createMessage("user", userContent);

        const assistantMessage = chat.createMessage("assistant");
        assistantMessage.startImplicitThinking();
        currentAssistantMessage = assistantMessage;
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });

        try {
            await api.postJson(`/api/v1/public/chat-clients/${chatClientKey}`, {
                messageData: message,
                attachments: safeAttachments.map(attach => {
                    return {
                        contentBase64: attach.contentBase64,
                        type: attach.type,
                        name: attach.name,
                        mimeType: attach.mimeType,
                        format: attach.format
                    }
                })
            }, {
                signal: abortController.signal
            });

            await startInference(assistantMessage);
        }
        catch (error) {
            if (error.name !== "AbortError") {
                assistantMessage.setError(error.message || chatClientData.value.culture.inferenceError);
            }
            assistantMessage.setDone();
            chat.isReadOnly = false;
            chat.isInferencing = false;
            currentAssistantMessage = null;
        }
    }

    async function handleCancel() {
        abortController?.abort();
        activeSse?.close();
        clearTimeout(activeInferenceRetryTimeout);
        await api.deleteJson(`/api/v1/public/chat-clients/${chatClientKey}`).catch(() => { });

        if (currentAssistantMessage) {
            currentAssistantMessage.setError(chatClientData.value.culture.inferenceCancelled);
            currentAssistantMessage.setDone();
        }

        chat.isReadOnly = false;
        chat.isInferencing = false;
        activeSse = null;
        activeInferenceRetryTimeout = null;
        currentAssistantMessage = null;
    }

    reloadMessages();

    return el.state.listening([chatClientData], () =>
        el("main.chat-page",
            neutralThemeStyles,
            DropOverlay(ev => {
                [...ev.dataTransfer.files].forEach(file => {
                    chat.addFile(file);
                });
            }),
            chatClientData.value && el.fragment(
                ChatV2({
                    messageList: chatClientData.value.messages,
                    onMessage: handleMessage,
                    onRetry: retryContext,
                    onClear: handleClear,
                    onSinthetize: chatClientData.value.client.audioSynthesisEnabled && handleSinthetize,
                    onCancel: handleCancel,
                    clientOptions: chatClientData.value.client,
                    culture: chatClientData.value.culture,
                    presentation: {
                        logoSrc: chatClientData.value.client.logoImageUrl,
                        helloTitle: chatClientData.value.client.helloLabel,
                        helloSubtitle: chatClientData.value.client.helloSubLabel
                    },
                    header: {
                        title: chatClientData.value.chatTitle || chatClientData.value.client.pageTitle
                    },
                    props: {
                        init() {
                            chat = this;
                        }
                    }
                }),
                customStyles,
                colorStyles
            )
        )
    )
}
